/**
 * sfc32 — a small, fast, well-distributed 32-bit PRNG. The state is four uint32s,
 * so it lives inside the serialisable sim state and survives snapshot/restore.
 */
export type RngState = [number, number, number, number];

/** Expand a 32-bit seed into a well-mixed state (splitmix32), then discard the first outputs. */
export function seedRng(seed: number, stream = 0): RngState {
  let x = (seed ^ Math.imul(stream + 1, 0x9e3779b9)) >>> 0;
  const next = () => {
    x = (x + 0x9e3779b9) >>> 0;
    let z = x;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
    return (z ^ (z >>> 16)) >>> 0;
  };
  const s: RngState = [next(), next(), next(), next()];
  for (let i = 0; i < 12; i++) nextU32(s);
  return s;
}

export function nextU32(s: RngState): number {
  const a = s[0], b = s[1], c = s[2], d = s[3];
  const t = (((a + b) >>> 0) + d) >>> 0;
  s[3] = (d + 1) >>> 0;
  s[0] = (b ^ (b >>> 9)) >>> 0;
  s[1] = (c + (c << 3)) >>> 0;
  const r = ((c << 21) | (c >>> 11)) >>> 0;
  s[2] = (r + t) >>> 0;
  return t;
}

/** Uniform in [0, 1). */
export const random = (s: RngState) => nextU32(s) / 4294967296;
/** Uniform in [a, b). */
export const range = (s: RngState, a: number, b: number) => a + random(s) * (b - a);
/** Integer in [0, n). */
export const int = (s: RngState, n: number) => Math.floor(random(s) * n);
